// F-V (2026-10-05): the batch "Generate Narration Audio" runner.
//
// Lives at the app root so a run keeps going while staff move between staff
// pages (a progress pill follows them). Closing / reloading the tab stops it
// — the browser's "Leave site?" prompt warns first — and because each slot
// is generated by the existing per-exhibit endpoint, everything finished is
// kept and a re-run picks up only what is still missing.
//
// Two slots at a time. The server's quota gate stays authoritative: if it
// reports the budget used up or generation switched off mid-run, the run
// halts and the rest are marked "Not started" instead of failing one by one.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@shared/api/client";
import { generateNarration, type NarrationKind } from "@shared/api/narration";
import { exhibitKeys } from "@shared/api/hooks/useExhibits";
import { zoneKeys } from "@shared/api/hooks/useZones";
import { aiUsageKey } from "@shared/api/hooks/useAiUsage";

export const RUN_CONCURRENCY = 2;

/** Server codes that mean "stop sending", not "this slot failed". */
const HALT_CODES: Record<string, string> = {
  "narration.generate.budget_exhausted": "Not started — monthly budget used up",
  "narration.generate.disabled": "Not started — AI narration switched off",
};

export interface RunItem {
  /** Exhibit or zone (F-W) — picks the generate endpoint + edit page. */
  kind: NarrationKind;
  ownerId: string;
  code: string;
  titleTc: string;
  language: string;
  chars: number;
}

export interface RunOutcome {
  item: RunItem;
  message: string;
}

export interface RunState {
  /** Increments per run — lets the UI reset per-run view state. */
  id: number;
  phase: "running" | "finished";
  total: number;
  done: number;
  current: RunItem | null;
  succeeded: RunItem[];
  failed: RunOutcome[];
  notStarted: RunOutcome[];
}

interface NarrationRunApi {
  run: RunState | null;
  isRunning: boolean;
  start: (items: RunItem[]) => void;
  stop: () => void;
  dismiss: () => void;
}

const NarrationRunContext = createContext<NarrationRunApi | null>(null);

export const useNarrationRun = (): NarrationRunApi => {
  const ctx = useContext(NarrationRunContext);
  if (!ctx) throw new Error("useNarrationRun outside NarrationRunProvider");
  return ctx;
};

/** The cached list-row shape the live badge update touches. */
interface ListRow {
  id: string;
  narrationStatus?: Record<string, string> | null;
}

const errorMessage = (err: unknown): string =>
  err instanceof Error && err.message ? err.message : "Request failed";

export const NarrationRunProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const qc = useQueryClient();
  const [run, setRun] = useState<RunState | null>(null);
  const stopRef = useRef<string | null>(null);
  const runSeq = useRef(0);
  const isRunning = run?.phase === "running";

  // Mark one slot current in every cached exhibits list, so the badges
  // update live as the run progresses.
  const markGenerated = useCallback(
    (item: RunItem) => {
      const listKey =
        item.kind === "exhibit" ? exhibitKeys.list() : zoneKeys.list();
      qc.setQueriesData<unknown>({ queryKey: listKey }, (old: unknown) =>
        Array.isArray(old)
          ? (old as ListRow[]).map((row) =>
              row.id === item.ownerId && row.narrationStatus
                ? {
                    ...row,
                    narrationStatus: {
                      ...row.narrationStatus,
                      [item.language]: "current",
                    },
                  }
                : row,
            )
          : old,
      );
    },
    [qc],
  );

  const start = useCallback(
    (items: RunItem[]) => {
      if (!items.length || isRunning) return;
      stopRef.current = null;
      runSeq.current += 1;
      const state: RunState = {
        id: runSeq.current,
        phase: "running",
        total: items.length,
        done: 0,
        current: items[0],
        succeeded: [],
        failed: [],
        notStarted: [],
      };
      setRun({ ...state });
      const queue = [...items];
      const publish = () => setRun({ ...state });

      const worker = async () => {
        for (let item = queue.shift(); item; item = queue.shift()) {
          if (stopRef.current) {
            state.notStarted.push({ item, message: stopRef.current });
            state.done += 1;
            publish();
            continue;
          }
          state.current = item;
          publish();
          try {
            await generateNarration(item.kind, item.ownerId, item.language);
            state.succeeded.push(item);
            markGenerated(item);
          } catch (err) {
            const halt =
              err instanceof ApiError ? HALT_CODES[err.code] : undefined;
            if (halt) {
              stopRef.current = halt;
              state.notStarted.push({ item, message: halt });
            } else {
              state.failed.push({ item, message: errorMessage(err) });
            }
          }
          state.done += 1;
          publish();
        }
      };

      void Promise.all(
        Array.from({ length: Math.min(RUN_CONCURRENCY, items.length) }, worker),
      ).then(async () => {
        state.phase = "finished";
        state.current = null;
        await Promise.all([
          qc.invalidateQueries({ queryKey: exhibitKeys.all }),
          qc.invalidateQueries({ queryKey: zoneKeys.all }),
          qc.invalidateQueries({ queryKey: aiUsageKey }),
        ]);
        if (!state.failed.length && !state.notStarted.length) {
          toast.success(
            `✓ ${state.succeeded.length} narration audio generated`,
          );
          setRun(null);
        } else {
          setRun({ ...state });
        }
      });
    },
    [isRunning, markGenerated, qc],
  );

  const stop = useCallback(() => {
    stopRef.current = "Not started — stopped";
  }, []);
  const dismiss = useCallback(() => setRun(null), []);

  // Warn before the tab closes / reloads mid-run.
  useEffect(() => {
    if (!isRunning) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isRunning]);

  const api = useMemo(
    () => ({ run, isRunning, start, stop, dismiss }),
    [run, isRunning, start, stop, dismiss],
  );
  return (
    <NarrationRunContext.Provider value={api}>
      {children}
    </NarrationRunContext.Provider>
  );
};
